// Dialer start screen: live campaigns I'm assigned to, certification per campaign, my numbers today (SPEC §15.1).
import { useEffect } from 'preact/hooks';
import type { Campaign, Certification } from '../../../src/shared/api';
import { navigate } from '../app';
import { Empty, ErrorBanner } from '../components/ui';
import { Spinner, useAsync, useExternal } from '../components/ui-extra';
import { useMe } from '../me';
import { phone } from '../phone/manager';
import { usePhone } from '../phone/PhoneProvider';
import { endpoints } from './endpoints';
import { checkPending, getDialer, refreshToday, rememberedCampaignId, startCampaign, subscribeDialer } from './store';

function CertChip(props: { cert: Certification | undefined; loaded: boolean }) {
  const c = props.cert;
  if (!props.loaded) return <span class="chip">Certification…</span>;
  if (!c) return <span class="chip chip-warn">Certification unknown</span>;
  if (c.exempt) return <span class="chip chip-info">Certification exempt</span>;
  if (c.certified) return <span class="chip chip-ok">Certified</span>;
  return (
    <span class="chip chip-warn" title={`Pass ${c.required.sessions} practice calls across ${c.required.kinds} persona types, once per niche (it doesn't expire).`}>
      Not certified · {c.passedSessions}/{c.required.sessions} passes, {c.personaKindsPassed.length}/{c.required.kinds} types
    </span>
  );
}

export function Picker() {
  const me = useMe();
  const d = useExternal(subscribeDialer, getDialer);
  const p = usePhone();
  const campaigns = useAsync(() => endpoints.campaigns(), []);
  const certs = useAsync(() => endpoints.certifications(me.id), [me.id]);
  useEffect(() => { void refreshToday(); }, []);

  const live = (campaigns.data ?? []).filter((c) => c.status === 'live' && (me.role === 'admin' || c.userIds.includes(me.id)));
  const remembered = rememberedCampaignId();
  live.sort((a, b) => (a.id === remembered ? -1 : b.id === remembered ? 1 : a.name.localeCompare(b.name)));
  const certFor = (c: Campaign) => certs.data?.find((x) => x.campaignId === c.id);
  const mine = d.today?.mine;

  return (
    <div class="vd-picker">
      <div class="row" style="justify-content:space-between;align-items:flex-end;flex-wrap:wrap;gap:12px">
        <div>
          <h1 style="margin:0">Dial</h1>
          <p class="muted" style="margin:4px 0 0">Pick a campaign. Nothing dials until you press Space or D on a lead.</p>
        </div>
        {mine ? (
          <div class="vd-stats" aria-label="My numbers today">
            <div><b>{mine.dials}</b><span>dials</span></div>
            <div><b>{mine.connects}</b><span>connects</span></div>
            <div><b>{mine.dmConversations}</b><span>DM talks</span></div>
            <div><b>{mine.meetingsBooked}</b><span>meetings</span></div>
            <div><b>{Math.round(mine.talkMinutes)}</b><span>talk min</span></div>
          </div>
        ) : null}
      </div>

      {d.pending === 'checking' || d.pending === 'unchecked' ? <p class="muted"><Spinner /> Checking for an unfinished call…</p> : null}
      {d.pending === 'error' ? (
        <div class="card vd-block" style="margin:12px 0">
          <ErrorBanner error={d.pendingError} />
          <p class="muted" style="margin:0 0 8px">Dialing stays locked until this check works, so no call goes unlogged.</p>
          <button class="btn btn-sm" onClick={() => void checkPending()}>Check again</button>
        </div>
      ) : null}

      {p.status === 'ready' && p.mic !== 'granted' ? (
        <div class="card vd-hint-card">
          <span>Before your block, put your headset on and test the microphone so the first call doesn't stall on a permission prompt.</span>
          <button class="btn btn-sm" onClick={() => void phone.requestMic()}>Test microphone</button>
        </div>
      ) : null}
      {p.status === 'not_configured' || p.status === 'unsupported' ? <ErrorBanner error={p.message} /> : null}
      {p.status === 'not_configured' && p.detail && me.role === 'admin' ? <p class="vd-field-hint" style="margin:-6px 0 10px">Admin detail: {p.detail}</p> : null}

      <ErrorBanner error={campaigns.error ? `Couldn't load campaigns: ${campaigns.error}` : null} />
      <ErrorBanner error={certs.error ? `Couldn't load your certification status (the server still checks it on every dial): ${certs.error}` : null} />

      {campaigns.loading && !campaigns.data ? <p class="muted"><Spinner /> Loading campaigns…</p> : null}
      {campaigns.data && live.length === 0 ? (
        <Empty>No live campaigns are assigned to you right now. Ask PJ to assign you, or check Training.</Empty>
      ) : null}

      <div class="vd-campaigns">
        {live.map((c) => {
          const cert = certFor(c);
          const blocked = !!cert && !cert.certified && !cert.exempt;
          return (
            <div class="card vd-campaign">
              <div class="row" style="justify-content:space-between;gap:8px;align-items:flex-start">
                <div>
                  <h3 style="margin:0">{c.name}</h3>
                  <div class="muted" style="font-size:13px">{[c.geoLabel, c.country === 'MIXED' ? 'US + Canada' : c.country, c.weekStart ? `week of ${c.weekStart}` : null].filter(Boolean).join(' · ')}</div>
                </div>
                <CertChip cert={cert} loaded={!certs.loading || !!certs.data} />
              </div>
              {c.script?.summary ? <p style="margin:8px 0;font-size:14px">{c.script.summary}</p> : null}
              {c.counts ? (
                <div class="muted" style="font-size:13px">
                  {c.counts.new} new · {c.counts.inProgress} in progress · {c.counts.callback} callbacks · {c.counts.meeting} meetings
                </div>
              ) : null}
              <div class="row" style="margin-top:10px;gap:8px">
                <button class="btn btn-primary" disabled={blocked || d.pending !== 'clear'}
                  title={blocked ? 'Pass certification on the Training page first.' : undefined}
                  onClick={() => void startCampaign(c.id)}>Start dialing</button>
                {blocked ? <button class="btn btn-sm" onClick={() => navigate('/training')}>Go to Training</button> : null}
              </div>
            </div>
          );
        })}
      </div>

      {d.today?.leaderboard?.length ? (
        <div style="margin-top:20px;max-width:560px">
          <div class="eyebrow" style="margin-bottom:6px">Team today</div>
          <table>
            <thead><tr><th>Setter</th><th>Dials</th><th>Connects</th><th>DM talks</th><th>Meetings</th></tr></thead>
            <tbody>
              {d.today.leaderboard.map((r) => (
                <tr class={r.userId === me.id ? 'vd-me-row' : ''}><td>{r.name}</td><td>{r.dials}</td><td>{r.connects}</td><td>{r.dmConversations}</td><td>{r.meetings}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
